import { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Space, Card, Row, Col } from "antd";
import { SearchOutlined, ClearOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import locationService from "../services/locationService";

const { Search } = Input;
// Removed unused Option destructuring

const Cities = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [deletingCity, setDeletingCity] = useState(null);
  const [form] = Form.useForm();
  
  // Arama ve filtreleme state'leri
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    name: '',
    countryId: null
  });

  // Şehirleri çek
  const fetchCities = async () => {
    setLoading(true);
    try {
      const result = await locationService.getAllCities();
      setCities(result);
      setFilteredCities(result);
    } catch {
      toast.error("Şehirler alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  // Ülkeleri çek (ülke seçimi için)
  const fetchCountries = async () => {
    try {
      const result = await locationService.getAllCountries();
      setCountries(result);
    } catch {
      toast.error("Ülkeler alınamadı.");
    }
  };

  // Genel arama fonksiyonu
  const handleSearch = (value) => {
    setSearchText(value);
    applyFilters(value, filters);
  };

  // Detaylı filtreleme fonksiyonu
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    applyFilters(searchText, newFilters);
  };

  // Filtreleri uygula
  const applyFilters = (search, detailFilters) => {
    let filtered = [...cities];

    // Genel arama (şehir adı ve ülke adında ara)
    if (search) {
      filtered = filtered.filter(city => {
        const countryName = countries.find(c => c.id === city.countryId)?.name || '';
        return city.name?.toLowerCase().includes(search.toLowerCase()) ||
               countryName.toLowerCase().includes(search.toLowerCase());
      });
    }

    // Şehir adına göre filtre
    if (detailFilters.name) {
      filtered = filtered.filter(city => 
        city.name?.toLowerCase().includes(detailFilters.name.toLowerCase())
      );
    }

    // Ülkeye göre filtre
    if (detailFilters.countryId) {
      filtered = filtered.filter(city => city.countryId === detailFilters.countryId);
    }

    setFilteredCities(filtered);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchText('');
    setFilters({ name: '', countryId: null });
    setFilteredCities(cities);
  };

  // Ekle / Güncelle
  const handleAddOrUpdate = async (values) => {
    try {
      let response;
      if (editingCity) {
        response = await locationService.updateCity({ ...editingCity, ...values });
      } else {
        response = await locationService.addCity(values);
      }

      if (response?.Status) {
        toast.success(response?.Message || response?.MessageCode || "İşlem başarılı.");
        fetchCities();
        setIsModalOpen(false);
        form.resetFields();
        setEditingCity(null);
      } else {
        toast.error(response?.Message || "İşlem başarısız.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.Message || "İşlem sırasında hata oluştu.");
    }
  };

  // Sil
  const handleDelete = async (id) => {
    try {
      const response = await locationService.deleteCity(id);
      if (response?.Status) {
        toast.success(response?.Message || "Silme işlemi başarılı.");
        fetchCities();
      } else {
        toast.error(response?.Message || "Silme işlemi başarısız.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.Message || "Silme sırasında hata oluştu.");
    }
  };

  useEffect(() => {
    fetchCities();
    fetchCountries();
  }, []);

  // Filtrelerin aktif olup olmadığını kontrol et
  const hasActiveFilters = searchText || filters.name || filters.countryId;
  const resultCount = filteredCities.length;

  // Ülke adını getiren fonksiyon
  const getCountryName = (countryId) => {
    return countries.find(c => c.id === countryId)?.name || "-";
  };

  const columns = [
    { 
      title: "Şehir Adı", 
      dataIndex: "name",
      sorter: (a, b) => a.name?.localeCompare(b.name),
      render: (text, record) => (
        <span style={{ 
          backgroundColor: filters.name && text?.toLowerCase().includes(filters.name.toLowerCase()) ? '#fff2e8' : 'transparent',
          padding: '2px 4px',
          borderRadius: '3px'
        }}>
          {text}
        </span>
      )
    },
    {
      title: "Ülke",
      dataIndex: "countryId",
      sorter: (a, b) => getCountryName(a.countryId).localeCompare(getCountryName(b.countryId)),
      render: (countryId, record) => {
        const countryName = getCountryName(countryId);
        return (
          <span style={{ 
            backgroundColor: filters.countryId === countryId ? '#fff2e8' : 'transparent',
            padding: '2px 4px',
            borderRadius: '3px'
          }}>
            {countryName}
          </span>
        );
      }
    },
    {
      title: "İşlem",
      width: 180,
      render: (text, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingCity(record);
              form.setFieldsValue(record);
              setIsModalOpen(true);
            }}
          >
            Güncelle
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setDeletingCity(record);
              setIsDeleteModalOpen(true);
            }}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Şehirler</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Yeni Şehir Ekle
        </Button>
      </div>

      {/* Arama ve Filtre Alanı */}
      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Genel Arama */}
          <div>
            <Search
              placeholder="Şehir adı veya ülkeye göre ara..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%', maxWidth: '400px' }}
              enterButton={<SearchOutlined />}
              allowClear
            />
          </div>

          {/* Detaylı Filtreler */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Input
                placeholder="Şehir adına göre filtrele"
                prefix={<SearchOutlined />}
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12}>
              <Select
                placeholder="Ülkeye göre filtrele"
                value={filters.countryId}
                onChange={(value) => handleFilterChange('countryId', value)}
                allowClear
                showSearch
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={countries.map(country => ({
                  value: country.id,
                  label: country.name
                }))}
              />
            </Col>
          </Row>

          {/* Filtre Durumu ve Temizle Butonu */}
          {hasActiveFilters && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#666' }}>
                <strong>{resultCount}</strong> şehir bulundu
                {cities.length !== resultCount && ` (${cities.length} toplam)`}
              </span>
              <Button 
                icon={<ClearOutlined />} 
                onClick={clearFilters}
                size="small"
              >
                Filtreleri Temizle
              </Button>
            </div>
          )}
        </Space>
      </Card>

      {/* Tablo */}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredCities}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} / ${total} kayıt`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        locale={{
          emptyText: hasActiveFilters 
            ? 'Filtrelere uygun şehir bulunamadı' 
            : 'Henüz şehir bulunmuyor'
        }}
      />

      {/* Ekle / Güncelle Modalı */}
      <Modal
        title={editingCity ? "Şehir Güncelle" : "Şehir Ekle"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingCity(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingCity ? "Güncelle" : "Ekle"}
        cancelText="İptal"
      >
        <Form form={form} onFinish={handleAddOrUpdate} layout="vertical">
          <Form.Item 
            name="name" 
            label="Şehir Adı" 
            rules={[
              { required: true, message: 'Şehir adı gerekli' },
              { min: 2, message: 'En az 2 karakter olmalı' }
            ]}
          >
            <Input placeholder="Örn: İstanbul" />
          </Form.Item>
          <Form.Item 
            name="countryId" 
            label="Ülke" 
            rules={[{ required: true, message: 'Ülke seçimi gerekli' }]}
          >
            <Select
              placeholder="Ülke Seç"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={countries.map(c => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Silme Modalı */}
      <Modal
        title="Silme işlemini onaylıyor musunuz?"
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onOk={() => {
          if (deletingCity) {
            handleDelete(deletingCity.id);
            setIsDeleteModalOpen(false);
          }
        }}
        okText="Evet, Sil"
        okType="danger"
        cancelText="Vazgeç"
      >
        <p>
          <strong>"{deletingCity?.name}"</strong> adlı şehir kalıcı olarak silinecek. Bu işlem geri alınamaz.
        </p>
        <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
          Bu şehre bağlı kullanıcı verileri etkilenebilir.
        </p>
      </Modal>
    </div>
  );
};

export default Cities;